import { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { appRoutes } from '@/constants'

const CAR_CATEGORIES = [
  {
    id: 'cat-sedan',
    label: 'SEDAN',
    image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?q=80&w=400&auto=format&fit=crop',
  },
  {
    id: 'cat-suv',
    label: 'SUV',
    image: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?q=80&w=400&auto=format&fit=crop',
  },
  {
    id: 'cat-coupe',
    label: 'COUPE',
    image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=400&auto=format&fit=crop',
  },
  {
    id: 'cat-convertible',
    label: 'CONVERTIBLE',
    image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?q=80&w=400&auto=format&fit=crop',
  },
  {
    id: 'cat-porsche',
    label: 'PORSCHE',
    image: 'https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?q=80&w=400&auto=format&fit=crop',
  },
  {
    id: 'cat-bmw',
    label: 'BMW',
    image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?q=80&w=400&auto=format&fit=crop',
  },
  {
    id: 'cat-audi',
    label: 'AUDI',
    image: 'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?q=80&w=400&auto=format&fit=crop',
  },
  {
    id: 'cat-mercedes',
    label: 'MERCEDES',
    image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?q=80&w=400&auto=format&fit=crop',
  },
]

export function ShopByCarCategorySection() {
  const navigate = useNavigate()
  const trackRef = useRef<HTMLDivElement>(null)

  // Infinite auto-scroll (same as SearchByCategorySection)
  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    let animId: number
    let pos = 0
    const speed = 0.55
    const step = () => {
      pos += speed
      const halfWidth = track.scrollWidth / 2
      if (pos >= halfWidth) pos = 0
      track.style.transform = `translateX(-${pos}px)`
      animId = requestAnimationFrame(step)
    }
    animId = requestAnimationFrame(step)
    return () => cancelAnimationFrame(animId)
  }, [])

  const allItems = [...CAR_CATEGORIES, ...CAR_CATEGORIES]

  return (
    <section className="py-10" style={{ backgroundColor: 'var(--color-bg-main)' }}>
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">

        {/* Heading row */}
        <div className="flex items-end justify-between mb-7">
          <div>
            <h2
              style={{
                fontFamily: "'DM Sans', 'Poppins', sans-serif",
                fontWeight: 800,
                fontSize: '20px',
                letterSpacing: '-0.01em',
                lineHeight: 1.1,
                color: 'var(--color-text-primary)',
                textTransform: 'uppercase',
              }}
            >
              SHOP BY <span style={{ color: '#CF2F2F' }}>CAR CATEGORY</span>
            </h2>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 400,
                fontSize: '13px',
                color: 'var(--color-text-secondary)',
                marginTop: '5px',
              }}
            >
              High-performance spares and replacements for your build.
            </p>
          </div>
          <button
            onClick={() => navigate(appRoutes.cars)}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700,
              fontSize: '12px',
              color: '#CF2F2F',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              letterSpacing: '0.04em',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              textTransform: 'uppercase',
            }}
          >
            SEE MORE <ArrowRight size={12} />
          </button>
        </div>

        {/* Auto-scroll carousel */}
        <div style={{ overflow: 'hidden', width: '100%' }}>
          <div
            ref={trackRef}
            style={{
              display: 'flex',
              gap: '14px',
              willChange: 'transform',
              width: 'max-content',
            }}
          >
            {allItems.map((cat, i) => (
              <button
                key={`${cat.id}-${i}`}
                type="button"
                onClick={() => navigate(appRoutes.cars)}
                className="relative overflow-hidden"
                style={{
                  flexShrink: 0,
                  width: '148px',
                  height: '100px',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border-subtle)',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'box-shadow 0.2s, border-color 0.2s',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = '#CF2F2F'
                  ;(e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(207,47,47,0.18)'
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border-subtle)'
                  ;(e.currentTarget as HTMLElement).style.boxShadow = 'none'
                }}
              >
                {/* Car image background */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-300"
                  style={{ backgroundImage: `url('${cat.image}')` }}
                />
                {/* Gradient overlay */}
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.1) 60%)' }}
                />
                {/* Label */}
                <span
                  className="absolute bottom-0 left-0 right-0 text-center"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 700,
                    fontSize: '11px',
                    color: '#fff',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    padding: '8px 4px',
                  }}
                >
                  {cat.label}
                </span>
              </button>
            ))}
          </div>
        </div>

      </div>
    </section>
  )
}
